/**
 * Auth domain types — single source of truth for user, role, and auth state.
 *
 * Convention: types live in src/types/<domain>.types.ts. Stores, components,
 * and services import types from here; they do not redefine them locally.
 *
 * Why this file exists: yesterday's survey revealed types defined inside
 * stores (UserRole, User, Company in authStore.ts) which couples type usage
 * to store usage and invites silent drift. This file (and its siblings)
 * is the single source.
 *
 * SCOPE NOTE: Company and Subscription types are TEMPORARILY kept here.
 * They'll move to subscription.types.ts and company.types.ts when we build
 * the pricing/onboarding flows in Phase 1. Logged as transient debt.
 */

// ────────────────────────────────────────────────────────────────────
// USER ROLE — four-role enum (matches backend exactly)
// ────────────────────────────────────────────────────────────────────

export type UserRole =
  | "tenant_owner"
  | "super_admin"
  | "business_admin"
  | "staff"
  | "cashier";

/** True when role is the platform owner (one per company). */
export const isTenantOwner = (r: UserRole): boolean => r === "tenant_owner";

/** True when role is a business-scoped admin (one per business). */
export const isBusinessAdmin = (r: UserRole): boolean => r === "business_admin";

/** True when role is operational staff (not admin, not cashier). */
export const isStaff = (r: UserRole): boolean => r === "staff";

/** True when role is a cashier (point-of-sale operator). */
export const isCashier = (r: UserRole): boolean => r === "cashier";

/**
 * True for any admin-level role. Use this for "can access admin UI" gates
 * where business_admin and tenant_owner should both pass.
 */
export const isAnyAdmin = (r: UserRole): boolean =>
  r === "tenant_owner" || r === "business_admin" || r==="super_admin";

/** Convenience for "can create/edit/cancel orders" gates. */
export const canManageOrders = (r: UserRole): boolean =>
  r === "tenant_owner" || r === "business_admin" || r === "cashier";

/**
 * Convenience for "can edit completed orders" — the Phase F backend
 * primitive. Only tenant_owner authorized at the API.
 */
export const canEditCompletedOrders = (r: UserRole): boolean =>
  r === "tenant_owner" || r === 'super_admin';

// ────────────────────────────────────────────────────────────────────
// SUBSCRIPTION (transient — moves to subscription.types.ts in Phase 1)
// ────────────────────────────────────────────────────────────────────

export interface Subscription {
  id: string;
  plan: "basic" | "standard" | "premium" | "enterprise";
  startDate: string;
  endDate: string;
  status: "active" | "expired" | "canceled" | "trial";
  features: string[];
}

// ────────────────────────────────────────────────────────────────────
// COMPANY (transient — moves to company.types.ts in Phase 1)
// ────────────────────────────────────────────────────────────────────

export interface Company {
  id: string;
  name: string;
  logo?: string;
  address?: string;
  phoneNumber?: string;
  email: string;
  subscription: Subscription;
}

// ────────────────────────────────────────────────────────────────────
// USER — frontend mirror of the backend's User model + JWT payload
// ────────────────────────────────────────────────────────────────────

export interface User {
  /** MongoDB _id, serialized as string. */
  id: string;
  name: string;
  email: string;
  role: UserRole;

  /** Tenant scope — every user belongs to exactly one company. */
  companyId: string;

  /**
   * Business scope. Backend JWT carries this:
   *   - null for tenant_owner (they own the whole company, multiple businesses)
   *   - set for business_admin / staff / cashier (scoped to one business)
   * The frontend's interceptor reads from businessStore for the active
   * business (which may differ from this field for multi-business owners).
   */
  businessId: string | null;

  /** Optional populated company object — present after fetchUserProfile. */
  company?: Company;

  profileImage?: string;
  createdAt: string;
  lastLogin?: string;
  /** Owners: where to land after signing in. */
  homeView?: HomeView;
}

/** "pick" = the business list, "all" = All businesses, "business" = straight into one. */
export interface HomeView {
  mode: "pick" | "all" | "business";
  businessId: string | null;
}

// ────────────────────────────────────────────────────────────────────
// LOGIN RESPONSE — exact shape returned by POST /auth/login
// (verified empirically against running backend on 2026-05-19)
// ────────────────────────────────────────────────────────────────────

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  /** 2-step sign-in is on: show the code screen, then POST /auth/mfa/verify. */
  mfaRequired?: boolean;
  mfaToken?: string;
  /** The business requires 2-step and this person hasn't set it up yet. */
  mfaSetupRequired?: boolean;
  user: {
    _id: string; // backend returns _id, not id
    name: string;
    email: string;
    role: UserRole;
    companyId: string;
    businessId: string | null;
    profileImage?: string;
    createdAt?: string;
    lastLogin?: string;
    company?: Company;
    homeView?: { mode?: string; businessId?: string | null } | null;
  };
}

// ────────────────────────────────────────────────────────────────────
// REFRESH RESPONSE — POST /auth/refresh-token
// ────────────────────────────────────────────────────────────────────

export interface RefreshTokenResponse {
  accessToken: string;
  /** null when another tab just rotated it — keep the newest saved one. */
  refreshToken: string | null;
}

// ────────────────────────────────────────────────────────────────────
// AUTH STORE STATE — the Zustand slice shape
// ────────────────────────────────────────────────────────────────────

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  lastProfileFetchAt: number | null;
  /** Password was right; waiting for the 2-step code. */
  mfaPending: { mfaToken: string; email: string } | null;
  /** Must set up 2-step before using the app (business policy). */
  mfaSetupRequired: boolean;

  // Methods (return types kept loose to avoid coupling here)
  login: (email: string, password: string) => Promise<boolean | "mfa">;
  /** Finish any sign-in (password, phone code): tokens, or the 2-step screen. */
  completeSignIn: (data: Partial<LoginResponse>, email?: string) => Promise<boolean | "mfa">;
  verifyMfa: (input: { code?: string; recoveryCode?: string }) => Promise<{ ok: boolean; recoveryCodesLeft?: number | null }>;
  cancelMfa: () => void;
  mfaSetupDone: (accessToken: string | null) => void;
  logout: () => Promise<void>;
  createCashier: (
    cashierData: Partial<User>,
    password: string,
  ) => Promise<unknown>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (token: string, password: string) => Promise<void>;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  fetchUserProfile: (options?: { force?: boolean }) => Promise<void>;
  refreshAuthToken: () => Promise<boolean>;

  // Setters (kept exposed for AuthContext compatibility during migration)
  setUser: (user: User | null) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
}
